import { CalculateMetadataFunction } from "remotion";
import { getThemeColors } from "@code-hike/lighter";
import type { SlideshowProps, SlideWithTiming } from "../slideshow-props";
// import parser; use any to avoid declaration-file resolution issues in this environment
const { parse } = require("@slidev/parser");



export const calculateMetadata: CalculateMetadataFunction<
  SlideshowProps
> = async ({ props }) => {
  // Try to read fps from Remotion's injection points; fall back to 30
  const fps = (props as any).__remotion?.fps || 30;

  // Defaults (align with Slideshow defaults)
  const defaultDuration = (props as any).defaultDuration ?? 5;
  const defaultTransitionDuration = (props as any).defaultTransitionDuration ?? 30;

  const themeColors = await getThemeColors((props as any).theme);

  // Parse markdown into slides
  let slides: Array<{ content: string; frontmatter: Record<string, any>; title?: string }> = [];
  if (typeof props.markdown === "string" && props.markdown.trim() !== "") {
    try {
      const parsed = parse(props.markdown);
      slides = parsed.slides.filter(
        (s: any) => s.content.trim() !== "" || Object.keys(s.frontmatter || {}).length > 0,
      );
    } catch (err) {
      console.warn("Failed to parse markdown in calculateMetadata, falling back to empty slides", err);
      slides = [];
    }
  }

  // Compute per-slide durations in frames and attach to slides
  const slidesWithTiming: SlideWithTiming[] = slides.map((s) => {
    const durationInFrames = (s.frontmatter?.duration ?? defaultDuration) * fps;
    // Transition duration is specified as frames in this project (e.g., 30)
    const transitionDurationInFrames = s.frontmatter?.transitionDuration ?? defaultTransitionDuration;
    return {
      ...s,
      durationInFrames,
      transitionDurationInFrames,
    };
  });

  const totalSlideFrames = slidesWithTiming.reduce((acc, s) => acc + s.durationInFrames, 0);
  const totalTransitionFrames = slidesWithTiming.slice(0, -1).reduce((acc, s) => acc + (s.transitionDurationInFrames ?? 0), 0);

  // total duration: sum(slide frames) - sum(transition frames) due to overlap in TransitionSeries
  const durationInFrames = Math.max(1, Math.floor(totalSlideFrames - totalTransitionFrames));

  // console.log("Calculated durationInFrames:", {
  //   fps,
  //   slideCount: slides.length,
  //   slides,
  //   totalSlideFrames,
  //   totalTransitionFrames,
  //   durationInFrames,
  // });

  const minimumWidth = (props as any).width?.type === "fixed" ? 0 : 1080;
  const minimumWidthApplied = minimumWidth;

  return {
    durationInFrames,
    width:
      (props as any).width?.type === "fixed"
        ? Math.max(minimumWidthApplied, (props as any).width?.value ?? 0)
        : minimumWidthApplied,
    markdown: props.markdown,
    props: {
      slides: slidesWithTiming,
      theme: (props as any).theme,
      width: (props as any).width,
      themeColors,
      markdown: props.markdown,
    },
  };
};
